import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { THEMES } from "@/lib/theme";
import { LaunchTransition } from "@/components/LaunchTransition";

const CARDS = [
  {
    id: "baking_jays",
    blurb: "Small-batch fantasy pastries — legendary cakes, cookies & breads, made to order.",
    image:
      "https://images.unsplash.com/photo-1578985545062-69928b1d9587?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
  {
    id: "rubys",
    blurb: "Slow-cooked entrées, elegant sides & catering platters. Hot food, warm hospitality.",
    image:
      "https://images.unsplash.com/photo-1555939594-58d7cb561ad1?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  },
];

export default function PickBusiness() {
  const { setSelectedBusinessId, selectedBusinessId } = useAuth();
  const nav = useNavigate();
  const [transitioning, setTransitioning] = useState(null);

  const pick = (id) => {
    if (transitioning) return;
    setSelectedBusinessId(id);
    setTransitioning(id);
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#0b0d16" }}>
      {transitioning && (
        <LaunchTransition businessId={transitioning} onDone={() => nav("/shop")} />
      )}
      <div className="container-app pt-16 pb-8 text-center">
        <div className="text-xs uppercase tracking-[0.4em] mb-4" style={{ color: "#B5892E" }}>
          Two kitchens · one home
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-tight animate-fade-up">
          Choose your table
        </h1>
        <p className="mt-4 text-base max-w-xl mx-auto animate-fade-up" style={{ color: "#a7adba", animationDelay: "80ms" }}>
          Order fantasy pastries from Blue Dragon&rsquo;s Treats or a hot catered feast from Ruby&rsquo;s — you can switch anytime.
        </p>
      </div>

      <div className="container-app grid gap-6 md:grid-cols-2 pb-20 max-w-4xl">
        {CARDS.map((c, i) => {
          const t = THEMES[c.id];
          return (
            <button
              key={c.id}
              onClick={() => pick(c.id)}
              data-testid={`pick-${c.id}`}
              className="group relative text-left rounded-3xl overflow-hidden card-lift banner-rise"
              style={{ animationDelay: `${120 + i * 100}ms`, minHeight: 440 }}
            >
              <img src={c.image} alt={t.name} className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-110" />
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(180deg, ${t.vars["--hero-from"]}44, ${t.vars["--hero-from"]}f7)` }}
              />
              {/* animated shimmer sweep */}
              <div className="banner-shimmer" aria-hidden="true" />
              <div className="relative h-full flex flex-col items-center justify-end p-7 text-center" style={{ minHeight: 440 }}>
                {/* floating crest with pulsing glow */}
                <div className="crest-wrap mb-5">
                  <div
                    className="crest-glow"
                    style={{ background: `radial-gradient(circle, ${t.vars["--accent"]}66 0%, transparent 70%)` }}
                    aria-hidden="true"
                  />
                  <img
                    src={t.logo}
                    alt={t.name}
                    className="crest-float relative h-28 w-28 sm:h-32 sm:w-32 object-contain"
                    style={{ filter: "drop-shadow(0 10px 22px rgba(0,0,0,0.5))" }}
                  />
                </div>
                <div className="text-[11px] uppercase tracking-widest mb-1" style={{ color: t.vars["--accent"] }}>
                  {t.kind}
                </div>
                <h2 className="font-display text-3xl text-white">{t.name}</h2>
                <p className="mt-2 text-sm text-white/80 max-w-sm">{c.blurb}</p>
                <div className="mt-5 inline-flex items-center gap-2 font-semibold text-white">
                  Enter shop <ArrowRight size={18} className="transition group-hover:translate-x-1.5" />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {selectedBusinessId && (
        <div className="text-center pb-10">
          <button onClick={() => nav("/shop")} className="text-sm text-white/60 hover:text-white underline">
            Continue to {THEMES[selectedBusinessId].name}
          </button>
        </div>
      )}
    </div>
  );
}
